<template>
  <CModal
    :show.sync="isVisible"
    size="lg"
    :title="modalTitle"
  >
    <div>

      <div class="role-sections">
        <!-- Ruoli assegnati (non ereditati) -->
        <div v-if="assignedEditableRoles.length > 0" class="role-section mb-4">
          <h6 class="section-title">
            <translate translate-context="collaborators.modal">Assigned Roles</translate>
            <CIcon v-if="isInheritedUser" name="cilInfo" size="sm" class="ml-1" v-c-tooltip="$gettext('For inherited users, assigned roles cannot be removed')"/>
          </h6>
          <div class="role-grid">
            <div v-for="role in assignedEditableRoles" :key="role.id" class="role-card">
              <div>
                <div class="d-flex align-items-center">
                  <button
                    type="button"
                    class="role-button"
                    :class="getRoleButtonClass(role)"
                    :disabled="isRoleDisabled(role)"
                    @click="toggleRole(role)"
                    :title="getRoleTooltip(role)"
                  >
                    <span class="role-indicator"></span>
                    <CIcon v-if="role.scope" :name="CollaboratorScope.icon(role.scope)" size="sm" class="mr-1" />
                    <span class="role-name">{{ role.name }}</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-1 ml-1"
                    @click.stop="$emit('view-permissions', role)"
                    :title="$gettext('View role permissions')"
                  >
                    <CIcon name="cilInfo" size="sm"/>
                  </button>
                </div>
                <small v-if="role.scope" class="text-muted d-block ml-2 mt-1">{{ CollaboratorScope.label(role.scope) }}</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Ruoli ereditati (non editabili) -->
        <div v-if="inheritedRoles.length > 0" class="role-section mb-4">
          <h6 class="section-title text-muted">
            <translate translate-context="collaborators.modal">Inherited Roles</translate>
            <CIcon name="cilInfo" size="sm" class="ml-1" v-c-tooltip="$gettext('These roles cannot be modified at this level')"/>
          </h6>
          <div class="role-grid">
            <div v-for="role in inheritedRoles" :key="role.id" class="role-card">
              <button
                type="button"
                class="role-button role-active role-disabled"
                disabled
              >
                <span class="role-indicator"></span>
                <CIcon v-if="role.scope" :name="CollaboratorScope.icon(role.scope)" size="sm" class="mr-1" />
                <span class="role-name">{{ role.name }}</span>
              </button>
              <button
                type="button"
                class="btn btn-link btn-sm p-1 ml-1"
                @click.stop="$emit('view-permissions', role)"
                :title="$gettext('View role permissions')"
              >
                <CIcon name="cilInfo" size="sm"/>
              </button>
            </div>
          </div>
        </div>

        <!-- Ruoli Globali -->
        <div v-if="globalAvailableRoles.length > 0" class="role-section mb-4">
          <h6 class="section-title">
            <CIcon name="cilGlobeAlt" size="sm" class="mr-1"/>
            <translate translate-context="collaborators.modal">Global Roles</translate>
          </h6>
          <div class="role-grid">
            <div v-for="role in globalAvailableRoles" :key="role.id" class="role-card">
              <button
                type="button"
                class="role-button"
                :class="getRoleButtonClass(role)"
                @click="toggleRole(role)"
              >
                <span class="role-indicator"></span>
                <CIcon v-if="role.scope" :name="CollaboratorScope.icon(role.scope)" size="sm" class="mr-1" />
                <span class="role-name">{{ role.name }}</span>
              </button>
              <button
                type="button"
                class="btn btn-link btn-sm p-1 ml-1"
                @click.stop="$emit('view-permissions', role)"
                :title="$gettext('View role permissions')"
              >
                <CIcon name="cilInfo" size="sm"/>
              </button>
            </div>
          </div>
        </div>

        <!-- Ruoli Contract Manager -->
        <div v-if="contractManagerAvailableRoles.length > 0" class="role-section mb-4">
          <h6 class="section-title">
            <CIcon name="cilBriefcase" size="sm" class="mr-1"/>
            <translate translate-context="collaborators.modal">Contract Manager Roles</translate>
          </h6>
          <div class="role-grid">
            <div v-for="role in contractManagerAvailableRoles" :key="role.id" class="role-card">
              <button
                type="button"
                class="role-button"
                :class="getRoleButtonClass(role)"
                @click="toggleRole(role)"
              >
                <span class="role-indicator"></span>
                <CIcon v-if="role.scope" :name="CollaboratorScope.icon(role.scope)" size="sm" class="mr-1" />
                <span class="role-name">{{ role.name }}</span>
              </button>
              <button
                type="button"
                class="btn btn-link btn-sm p-1 ml-1"
                @click.stop="$emit('view-permissions', role)"
                :title="$gettext('View role permissions')"
              >
                <CIcon name="cilInfo" size="sm"/>
              </button>
            </div>
          </div>
        </div>

        <!-- Altri ruoli specifici dello scope -->
        <div v-if="otherScopeRoles.length > 0" class="role-section mb-4">
          <h6 class="section-title">
            <translate translate-context="collaborators.modal">Other Available Roles</translate>
            <span class="text-muted ml-2" v-if="scopeType !== 'global'">
              ({{ getScopeLabel() }})
            </span>
          </h6>
          <div class="role-grid">
            <div v-for="role in otherScopeRoles" :key="role.id" class="role-card">
              <button
                type="button"
                class="role-button"
                :class="getRoleButtonClass(role)"
                @click="toggleRole(role)"
              >
                <span class="role-indicator"></span>
                <CIcon v-if="role.scope" :name="CollaboratorScope.icon(role.scope)" size="sm" class="mr-1" />
                <span class="role-name">{{ role.name }}</span>
              </button>
              <button
                type="button"
                class="btn btn-link btn-sm p-1 ml-1"
                @click.stop="$emit('view-permissions', role)"
                :title="$gettext('View role permissions')"
              >
                <CIcon name="cilInfo" size="sm"/>
              </button>
            </div>
          </div>
        </div>

        <!-- Messaggio se non ci sono ruoli disponibili -->
        <div v-if="allAvailableRoles.length === 0 && assignedEditableRoles.length === 0 && inheritedRoles.length === 0" class="text-center text-muted py-4">
          <translate translate-context="collaborators.modal">No roles available to assign</translate>
        </div>
      </div>

    </div>

    <template #footer>
      <CButton v-if="!isInviteMode" color="primary" @click="close">
        <translate translate-context="collaborators.modal">Close</translate>
      </CButton>
      <template v-else>
        <CButton color="secondary" @click="close">
          <translate translate-context="collaborators.modal">Cancel</translate>
        </CButton>
        <CButton color="primary" @click="confirmSelection">
          <translate translate-context="collaborators.modal">Confirm Selection</translate>
        </CButton>
      </template>
    </template>
  </CModal>
</template>

<script>
import CollaboratorScope from '@/domain/core/constant/collaboratorScope'

export default {
  name: 'RoleSelectionModal',
  
  props: {
    availableRoles: {
      type: Array,
      required: true
    },
    scopeType: {
      type: String,
      required: true
    }
  },
  
  data() {
    return {
      CollaboratorScope,
      isVisible: false,
      title: '',
      currentRoles: [],
      selectedRoles: {},
      isInheritedUser: false,
      collaboratorId: null,
      isInviteMode: false,
      callback: null
    }
  },
  
  computed: {
    modalTitle() {
      return this.title || this.$gettext('Manage Roles')
    },
    
    // Merge dei ruoli correnti con i dati dai ruoli disponibili
    enrichedCurrentRoles() {
      return this.currentRoles.map(role => {
        const availableRole = this.availableRoles.find(r => r.id === role.id)
        return {
          ...role,
          // is_editable viene dal ruolo del collaboratore, non dalla lista disponibili
          is_editable: role.is_editable !== undefined ? role.is_editable : true,
          // is_global e is_contract_manager vengono dalla lista disponibili
          is_global: availableRole ? availableRole.is_global : false,
          is_contract_manager: availableRole ? availableRole.is_contract_manager : false
        }
      })
    },
    
    // Ruoli assegnati (non ereditati)
    assignedEditableRoles() {
      return this.enrichedCurrentRoles.filter(role => !role.is_inherited)
    },
    
    // Ruoli ereditati (non modificabili)
    inheritedRoles() {
      return this.enrichedCurrentRoles.filter(role => role.is_inherited)
    },
    
    // Ruoli globali disponibili
    globalAvailableRoles() {
      return this.getAvailableNotAssigned().filter(role => role.is_global === true)
    },
    
    // Ruoli Contract Manager disponibili  
    contractManagerAvailableRoles() {
      return this.getAvailableNotAssigned().filter(role => role.is_contract_manager === true)
    },
    
    // Altri ruoli specifici dello scope
    otherScopeRoles() {
      return this.getAvailableNotAssigned().filter(role => 
        !role.is_global && !role.is_contract_manager
      )
    },
    
    allAvailableRoles() {
      return [...this.globalAvailableRoles, ...this.contractManagerAvailableRoles, ...this.otherScopeRoles]
    },
    
    hasChanges() {
      // Verifica se ci sono modifiche rispetto ai ruoli iniziali
      const initialRoleIds = new Set(this.enrichedCurrentRoles
        .filter(r => !r.is_inherited) // Escludi ruoli ereditati dal confronto
        .map(r => String(r.id)))
      const selectedRoleIds = new Set(Object.keys(this.selectedRoles)
        .filter(id => this.selectedRoles[id] === true))
      
      // Controlla se ci sono differenze
      if (initialRoleIds.size !== selectedRoleIds.size) return true
      
      for (let id of initialRoleIds) {
        if (!selectedRoleIds.has(id)) return true
      }
      
      for (let id of selectedRoleIds) {
        if (!initialRoleIds.has(id)) return true
      }
      
      return false
    }
  },
  
  methods: {
    getRoleButtonClass(role) {
      const classes = []
      
      // Determina se il ruolo è attualmente selezionato
      const isSelected = this.isRoleSelected(role)
      
      if (isSelected) {
        classes.push('role-active')
      }
      
      if (this.isRoleDisabled(role)) {
        classes.push('role-disabled')
      }
      
      // Mostra pending se c'è una modifica pendente
      if (this.isPendingChange(role)) {
        classes.push('role-pending')
      }
      
      return classes.join(' ')
    },
    
    isRoleDisabled(role) {
      // Un ruolo è disabilitato se:
      // 1. Non è editabile dal backend
      // 2. È un ruolo assegnato a un utente ereditato (non può essere rimosso)
      // 3. È un ruolo ereditato (gestito a livello superiore)
      
      // Se il ruolo ha is_editable = false, è sempre disabilitato
      if (role.is_editable === false) {
        return true
      }
      
      // Per utenti ereditati, i ruoli già assegnati non possono essere rimossi
      if (this.isInheritedUser && this.isRoleSelected(role)) {
        // Verifica se il ruolo era già assegnato inizialmente
        const wasInitiallyAssigned = this.currentRoles.some(r => r.id === role.id && !r.is_inherited)
        if (wasInitiallyAssigned) {
          return true
        }
      }
      
      return false
    },
    
    getRoleTooltip(role) {
      if (this.isInheritedUser && this.isRoleSelected(role)) {
        const wasInitiallyAssigned = this.currentRoles.some(r => r.id === role.id && !r.is_inherited)
        if (wasInitiallyAssigned) {
          return this.$gettext('This role cannot be removed from inherited users')
        }
      }
      if (role.is_editable === false) {
        return this.$gettext('This role is read-only')
      }
      return ''
    },
    
    show(options) {
      // Opzioni:
      // - title: titolo personalizzato
      // - roles: ruoli attuali (array di oggetti ruolo)
      // - selectedRoles: ruoli selezionati (object con id: true/false)
      // - isInheritedUser: se l'utente è ereditato
      // - collaboratorId: ID del collaboratore (per emettere eventi di toggle)
      // - callback: funzione da chiamare in modalità invito
      
      this.title = options.title || this.$gettext('Manage Roles')
      this.currentRoles = options.roles || []
      this.isInheritedUser = options.isInheritedUser || false
      this.collaboratorId = options.collaboratorId || null
      this.callback = options.callback || null
      
      // Se c'è un callback ma non un collaboratorId, siamo in modalità invito
      this.isInviteMode = !this.collaboratorId && !!this.callback
      
      // Inizializza selectedRoles basandosi su currentRoles o selectedRoles passati
      // Usa Vue.set per garantire la reattività
      this.$set(this, 'selectedRoles', {})
      
      if (options.selectedRoles) {
        // Se vengono passati selectedRoles, usa quelli
        Object.entries(options.selectedRoles).forEach(([roleId, value]) => {
          this.$set(this.selectedRoles, String(roleId), value)
        })
      } else {
        // Altrimenti deriva dai ruoli correnti (tutti quelli non ereditati)
        this.currentRoles.forEach(role => {
          if (!role.is_inherited) {
            this.$set(this.selectedRoles, String(role.id), true)
          }
        })
        
        // Aggiungi anche i ruoli disponibili che potrebbero avere pending changes
        if (options.pendingChanges) {
          Object.entries(options.pendingChanges).forEach(([roleId, value]) => {
            this.$set(this.selectedRoles, String(roleId), value)
          })
        }
      }
      
      this.isVisible = true
    },
    
    hide() {
      this.isVisible = false
    },
    
    close() {
      this.hide()
    },
    
    getAvailableNotAssigned() {
      const currentRoleIds = new Set(this.enrichedCurrentRoles.map(r => r.id))
      return this.availableRoles.filter(role => !currentRoleIds.has(role.id))
    },
    
    isRoleSelected(role) {
      return this.selectedRoles[String(role.id)] === true
    },
    
    isPendingChange(role) {
      // Un ruolo ha una modifica pendente se:
      // - Era nei ruoli iniziali e ora non è selezionato
      // - Non era nei ruoli iniziali e ora è selezionato
      const wasInitially = this.enrichedCurrentRoles.some(r => r.id === role.id && !r.is_inherited)
      const isNowSelected = this.selectedRoles[String(role.id)] === true
      return wasInitially !== isNowSelected
    },
    
    toggleRole(role) {
      const roleIdStr = String(role.id)
      const currentState = this.isRoleSelected(role)
      
      // Trova il ruolo nei ruoli assegnati per verificare is_editable
      const assignedRole = this.enrichedCurrentRoles.find(r => r.id === role.id)
      
      // Se il ruolo è già assegnato e non è editabile, non permettere modifiche
      if (assignedRole && assignedRole.is_editable === false) {
        return
      }
      
      // Per utenti ereditati, non permettere la rimozione di ruoli già assegnati
      if (this.isInheritedUser && currentState) {
        // Controlla se il ruolo era già assegnato inizialmente (non ereditato)
        const wasInitiallyAssigned = this.currentRoles.some(r => r.id === role.id && !r.is_inherited)
        if (wasInitiallyAssigned) {
          // Non permettere la rimozione di un ruolo già assegnato per utenti ereditati
          return
        }
      }
      
      // Alterna lo stato del ruolo - usa sempre false invece di delete per mantenere la reattività
      const newValue = !currentState
      this.$set(this.selectedRoles, roleIdStr, newValue)
      
      // Se non siamo in modalità invito, emetti l'evento di toggle per aggiornare le modifiche pendenti
      if (!this.isInviteMode && this.collaboratorId) {
        this.$emit('toggle', this.collaboratorId, role.id, newValue)
      }
    },
    
    confirmSelection() {
      // Chiama il callback con i ruoli selezionati (modalità invito)
      if (this.callback) {
        this.callback(this.selectedRoles)
      }
      this.hide()
    },
    
    getScopeLabel() {
      switch(this.scopeType) {
        case 'contract-manager':
          return this.$gettext('Contract Manager')
        case 'pica-service':
          return this.$gettext('Event')
        default:
          return ''
      }
    }
  }
}
</script>

<style scoped>
.role-sections {
  max-height: 60vh;
  overflow-y: auto;
}

.role-section {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}

.role-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.75rem;
  color: #374151;
  display: flex;
  align-items: center;
}

.role-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

.role-card {
  display: flex;
  align-items: center;
}

.role-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
  flex-grow: 1;
}

.role-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.role-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.role-indicator {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: white;
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.role-name {
  font-weight: 500;
  color: #374151;
}

.role-button.role-active .role-indicator {
  background: #10b981;
  border-color: #10b981;
}

.role-button.role-active .role-indicator::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 10px;
  font-weight: bold;
}

.role-button.role-disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #f9fafb;
}

.role-button.role-disabled .role-name {
  color: #6b7280;
}

.role-button.role-disabled.role-active .role-indicator {
  background: #6b7280;
  border-color: #6b7280;
}

.role-button.role-pending {
  border-color: #f59e0b;
  background: #fffbeb;
}

.role-button.role-pending .role-indicator {
  border-color: #f59e0b;
}

.role-button.role-pending.role-active .role-indicator {
  background: #f59e0b;
  border-color: #f59e0b;
}

.bg-warning-light {
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
}
</style>